import React, { useCallback } from 'react';
import cn from 'classnames';
import DT from 'duration-time-conversion';
import * as utils from '../../../../../shared/utils';
import ActionsMarkers from './ActionsMarkers';
import OcrMarkers from './OcrMarkers';
import KeywordsMarkers from './KeywordsMarkers';
import s from './Markers.module.scss';

const Markers = ({
  duration,
  showMarkersTrack,
  isShowHistoryPanel,
  markers,
  isShowAiPanel,
  videoAiView,
  aiData,
  onSetCurrentTime,
}) => {
  const handleCommentMarker = useCallback((item, startKey = 'videoCurrentTime') => {
    const start = item[startKey];
    const raw = Array.isArray(start) ? start[0] : start;
    const seconds = typeof raw === 'string' ? DT.t2d(raw) : raw;
    return (seconds * 100) / duration;
  }, [duration]);

  const handleLeftStickyMarker = useCallback((item, startKey = 'videoCurrentTime') => {
    const raw = item[startKey];
    const seconds = typeof raw === 'string' ? DT.t2d(raw) : raw;
    return (seconds * 100) / duration < 100;
  }, [duration]);

  const handleRightStickyMarker = useCallback((item, startKey = 'videoCurrentTime') => {
    const raw = item[startKey];
    const seconds = typeof raw === 'string' ? DT.t2d(raw) : raw;
    const position = (seconds * 100) / duration;
    const threshold = duration ? ((duration - 100) * 100) / duration : 0;
    return position > threshold;
  }, [duration]);

  const handleClickMarker = useCallback((comment) => {
    const { videoCurrentTime } = comment;

    onSetCurrentTime(videoCurrentTime, true);
  }, [onSetCurrentTime]);

  if (!showMarkersTrack) return null;

  return (
    <div className={s.markersTrack}>
      <If condition={isShowHistoryPanel}>
        {markers
          && markers.map((comment, index) => (
            <div
              key={index}
              className={cn(s.itemMarker, {
                [s.leftStickyMarker]: handleLeftStickyMarker(comment),
                [s.rightStickyMarker]: handleRightStickyMarker(comment),
              })}
              style={{ left: `${handleCommentMarker(comment)}%` }}
              onClick={() => handleClickMarker(comment)}
              role="presentation"
            >
              <div className={s.itemMarkerData}>
                <div className={s.itemMarkerDataTitle}>{comment.userDisplayName}</div>
                <div className={s.itemMarkerDataText}>
                  <span className={s.itemMarkerDataTextTime}>
                    {utils.parseTime(comment.videoCurrentTime)}
                    <If condition={comment.videoCurrentTimeEnd}>
                      - {utils.parseTime(comment.videoCurrentTimeEnd)}
                    </If>
                  </span>{' '}
                  {comment.text.replace(utils.mentionPattern, (mentionString) => {
                    const mentionID = mentionString.substring(1);
                    const mention = comment.mentions
                      ? comment.mentions.find((m) => m._id === mentionID)
                      : null;
                    return mention ? `@${mention.displayName}` : mentionString;
                  })}
                </div>
              </div>
            </div>
          ))}
      </If>

      <If condition={isShowAiPanel}>
        <Choose>
          <When condition={videoAiView === 'actions' && aiData?.actions?.length}>
            <ActionsMarkers
              data={aiData.actions}
              setCurrentTime={onSetCurrentTime}
              handleLeftStickyMarker={handleLeftStickyMarker}
              handleRightStickyMarker={handleRightStickyMarker}
              handleCommentMarker={handleCommentMarker}
              assetId={aiData.assetId}
            />
          </When>
          <When condition={videoAiView === 'ocr' && aiData?.ocr?.length}>
            <OcrMarkers
              data={aiData.ocr}
              setCurrentTime={onSetCurrentTime}
              handleLeftStickyMarker={handleLeftStickyMarker}
              handleRightStickyMarker={handleRightStickyMarker}
              handleCommentMarker={handleCommentMarker}
              assetId={aiData.assetId}
            />
          </When>
          <When condition={videoAiView === 'keywords' && aiData?.keywords?.length}>
            <KeywordsMarkers
              data={aiData.keywords}
              setCurrentTime={onSetCurrentTime}
              handleLeftStickyMarker={handleLeftStickyMarker}
              handleRightStickyMarker={handleRightStickyMarker}
              handleCommentMarker={handleCommentMarker}
              assetId={aiData.assetId}
            />
          </When>
        </Choose>
      </If>
    </div>
  );
};

export default Markers;
